/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useUserQuery } from '../../actions/UserActions';

import { useDropzone } from 'react-dropzone';
import { useMeetingMinutesActions } from '../../actions/MeetingMinutesActions';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';

import ModelSelector from '../thread/ModelSelector';
import { track } from '../../utils/track';

import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Text from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';

import Switch from '../common/Switch';

function List({ items, depth = 0 }) {
  return (
    <ul>
      {items.map((item) => (
        <li
          key={item.id}
          css={css`
            margin-top: ${depth === 0 ? '1rem' : '0rem'};
          `}
        >
          <Text variant="body2" fontWeight={depth === 0 ? '600' : '400'}>
            {item.text}
          </Text>
          {item.children && <List items={item.children} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  );
}

function FileUpload({ onFile }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const { t } = useTranslation();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        '.docx',
    },
    multiple: false,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onFile(acceptedFiles[0]);
      }
    },
  });

  return (
    <React.Fragment>
      <div
        {...getRootProps()}
        css={css`
          background-color: ${isDragActive
            ? 'var(--primary-8p)'
            : 'transparent'};

          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex-grow: 2;

          border: 1px dashed
            ${isDragActive ? 'var(--primary-main)' : 'var(--text-secondary)'};
          border-radius: 8px;
          max-height: 262px;
          flex-grow: 1;
          margin: 1.5rem;
          cursor: pointer;
        `}
      >
        <input {...getInputProps()} />
        <UploadFileOutlinedIcon
          css={css`
            color: ${isDragActive
              ? 'var(--primary-main)'
              : 'var(--text-secondary)'};
          `}
          fontSize="large"
        />
        <Text variant="subtitle1" fontWeight="600">
          {t('Ladda upp ditt mötesprotokoll')}
        </Text>
        <Text variant="subtitle2" color="textSecondary">
          {t(
            'Dra och släpp filer, eller klicka här för att ladda upp från din dator'
          )}
        </Text>
      </div>
      <div
        css={css`
          background-color: rgba(238, 238, 238, 1);
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
          cursor: pointer;
        `}
        onClick={() => setOpenCollapse(!openCollapse)}
      >
        <div
          css={css`
            display: flex;

            justify-content: space-between;
          `}
        >
          <Text
            variant="subtitle2"
            fontWeight="600"
            color="textSecondary"
            css={css`
              margin-top: 1rem;
            `}
          >
            {t('Osäker på vilka filtyper du kan ladda upp?')}
          </Text>
          <IconButton
            onClick={() => setOpenCollapse(!openCollapse)}
            css={css`
              margin-top: 0.5rem;
            `}
          >
            {openCollapse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
        <Collapse in={openCollapse}>
          <Text
            variant="subtitle2"
            color="textSecondary"
            css={css`
              padding-bottom: 1rem;
            `}
          >
            {t(
              'Vi stdöjer endast uppladdning av wordfiler (docx). Mötesprotkollet ska vara formaterat som en numrerad eller punkt list. All annan text kommer vi att ignorera.'
            )}
          </Text>
        </Collapse>
      </div>
    </React.Fragment>
  );
}

function Preview({ file, preview, isLoading, onRemove, isPreviewError }) {
  const { t } = useTranslation();

  return (
    <div
      css={css`
        padding: 0.5rem;
        display: flex;
        gap: 1rem;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 1rem;
          align-items: center;

          background-color: var(--primary-8p);
          border-radius: 8px;
        `}
      >
        <AttachFileIcon color="primary" />
        <Text variant="subtitle2" fontWeight="600">
          {file.name}
        </Text>
        <Button
          color="text"
          endIcon={<CloseIcon />}
          css={css`
            margin-left: auto;
          `}
          onClick={onRemove}
        >
          {t('Ta bort fil')}
        </Button>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <Text variant="subtitle2" fontWeight="600" color="textSecondary">
          {t('Mötesprotokoll - förhandsvisning')}
        </Text>
        {isLoading ? (
          <div
            css={css`
              margin-top: 2rem;
              margin-left: auto;
              margin-right: auto;
            `}
          >
            <CircularProgress />
          </div>
        ) : isPreviewError || !preview.agenda.length ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              flex-direction: column;
              justify-content: center;
              max-width: 300px;
              margin: 2rem auto;
            `}
          >
            <ErrorIcon color="error" fontSize="large" />
            <Text variant="body1" color="textSecondary" align="center">
              {t(
                'Vi kunde inte hitta några mötespunkter. Vänligen kontrollera att du ladda upp rätt dokument.'
              )}
            </Text>
          </div>
        ) : (
          <div
            css={css`
              display: flex;
              gap: 20px;

              max-height: 360px;
              overflow-y: auto;
            `}
          >
            <List items={preview.agenda} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function CreateMeetingMinutesDocument({
  data,
  onClose,
  switchOptions,
  onSwitchChange,
}) {
  const { t } = useTranslation();
  const {
    createMinutesPreview,
    isPreviewLoading,
    previewData,
    uploadMinutesAsync,
    isPreviewError,
  } = useMeetingMinutesActions();
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const { user } = useUserQuery();

  async function onConfirm() {
    try {
      setLoading(true);

      track('meeting_minutes:create', {
        model: user?.default_model,
      });

      const formData = new FormData();
      formData.append('file', file);

      const res = await uploadMinutesAsync({
        form: formData,
        title,
        meetingId: data?.fileId,
      });

      setLoading(false);
      onClose(res?.docId);
    } catch (err) {
      setLoading(false);
    }
  }

  async function handleFile(_file) {
    const formData = new FormData();
    formData.append('file', _file);
    setFile(_file);
    if (!title) {
      setTitle(_file.name);
    }
    try {
      createMinutesPreview(formData);
      //onClose(res?.id);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <>
      <DialogContent>
        <div
          css={css`
            display: flex;
            gap: 2rem;
            height: 100%;
            flex-direction: row;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              max-width: 254px;
              gap: 1rem;
            `}
          >
            <Switch
              options={switchOptions}
              value="minutes"
              variant="outlined"
              onChange={() => onSwitchChange()}
            />
            <Text variant="h4" fontWeight="bold">
              {t('Skapa nytt mötesprotokoll')}
            </Text>
            <Text variant="body1" color="textSecondary">
              {t('Skapa ett nytt dokument genom att ladda upp agenda.')}
            </Text>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              label={t('Namnge ditt dokument')}
              InputProps={{
                sx: {
                  backgroundColor: '#fff',
                },
              }}
            />
          </div>
          <Paper
            css={css`
              flex-grow: 1;

              display: flex;
              flex-direction: column;

              border-radius: 16px;
              min-height: 360px;
            `}
            elevation={4}
          >
            {file ? (
              <Preview
                preview={previewData}
                isLoading={isPreviewLoading}
                file={file}
                onRemove={() => {
                  if (title === file.name) {
                    setTitle('');
                  }
                  setFile(null);
                }}
                isPreviewError={isPreviewError}
              />
            ) : (
              <FileUpload onFile={handleFile} />
            )}
          </Paper>
        </div>
      </DialogContent>
      <div
        css={css`
          display: flex;
          gap: 1rem;
          padding: 1rem;
          justify-content: space-between;
        `}
      >
        <ModelSelector />
        <div>
          <Button onClick={() => onClose(false)}>{t('Avbryt')}</Button>
          <Button
            onClick={onConfirm}
            disabled={
              loading ||
              !file ||
              !title ||
              !previewData?.agenda?.length ||
              isPreviewError
            }
            variant="contained"
            color="primary"
          >
            {t('Skapa dokument')}
            {loading ? <CircularProgress size={16} /> : ''}
          </Button>
        </div>
      </div>
    </>
  );
}
